/**
 * Static-mode API — drop-in axios replacement for FTP / no-backend deploys.
 *
 * Reads the bundled CMS snapshot from `${PUBLIC_URL}/cms-data/snapshot.json`,
 * serves all GET routes from it, and persists POST/PUT/DELETE/PATCH
 * mutations to localStorage so the admin demo "saves" survive reloads in the
 * same browser session.
 *
 * Public-asset URLs in the snapshot (e.g. /cms-data/...) are rewritten at
 * load time to include the PUBLIC_URL prefix so they resolve correctly
 * regardless of the path the site is hosted under (e.g. /new on FTP).
 */

const STORAGE_KEY = "cec-static-store-v1";
// PUBLIC_URL is "" when homepage is "." in CRA. Use a relative URL so this
// works under any subfolder on FTP without needing rewrite rules.
const SNAPSHOT_URL = `${process.env.PUBLIC_URL || "."}/cms-data/snapshot.json`;
const ADMIN_CFG_URL = `${process.env.PUBLIC_URL || "."}/cms-data/admin-config.json`;

const PUBLIC_PREFIX = process.env.PUBLIC_URL || ".";

// ---------------------------------------------------------------------------
// PBKDF2 helpers for admin login verification
// ---------------------------------------------------------------------------
async function pbkdf2Verify(password, cfg) {
  if (!cfg || !cfg.salt || !cfg.hash || !cfg.iterations) return false;
  const enc = new TextEncoder();
  const salt = Uint8Array.from(atob(cfg.salt), (c) => c.charCodeAt(0));
  const keyMat = await crypto.subtle.importKey(
    "raw",
    enc.encode(password),
    { name: "PBKDF2" },
    false,
    ["deriveBits"],
  );
  const bits = await crypto.subtle.deriveBits(
    { name: "PBKDF2", salt, iterations: cfg.iterations, hash: "SHA-256" },
    keyMat,
    256,
  );
  const bytes = new Uint8Array(bits);
  let bin = "";
  for (let i = 0; i < bytes.length; i += 1) bin += String.fromCharCode(bytes[i]);
  const hashed = btoa(bin);
  if (hashed.length !== cfg.hash.length) return false;
  let diff = 0;
  for (let i = 0; i < hashed.length; i += 1) {
    diff |= hashed.charCodeAt(i) ^ cfg.hash.charCodeAt(i);
  }
  return diff === 0;
}

let adminCfgPromise = null;
function loadAdminCfg() {
  if (!adminCfgPromise) {
    adminCfgPromise = fetch(ADMIN_CFG_URL, { cache: "no-cache" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .catch(() => null);
  }
  return adminCfgPromise;
}

function rewriteAssetUrl(value) {
  if (typeof value !== "string") return value;
  // Self-hosted asset paths that the snapshot stores as absolute root paths
  if (value.startsWith("/cms-data/") || value.startsWith("/brand/")) {
    return `${PUBLIC_PREFIX}${value}`;
  }
  return value;
}

function rewriteUrlsInDoc(doc) {
  if (!doc || typeof doc !== "object") return doc;
  const out = Array.isArray(doc) ? [] : {};
  for (const [k, v] of Object.entries(doc)) {
    if (v && typeof v === "object") {
      out[k] = rewriteUrlsInDoc(v);
    } else if (k === "photo_url" || k === "image_url" || k === "hero_image" || k === "url") {
      out[k] = rewriteAssetUrl(v);
    } else {
      out[k] = v;
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------
let storePromise = null;
let store = null; // { departments:[], services:[], lawyers:[], insights:[], contact:[] }
const listeners = new Set();

async function loadStore() {
  if (store) return store;
  if (storePromise) return storePromise;

  storePromise = (async () => {
    let base;
    try {
      const local = localStorage.getItem(STORAGE_KEY);
      if (local) base = JSON.parse(local);
    } catch (e) { /* ignore */ }

    if (!base) {
      const res = await fetch(SNAPSHOT_URL, { cache: "no-cache" });
      if (!res.ok) throw new Error(`Snapshot fetch failed: ${res.status}`);
      base = await res.json();
      base.contact = base.contact || [];
    }

    // Rewrite asset urls
    base.departments = (base.departments || []).map(rewriteUrlsInDoc);
    base.services    = (base.services || []).map(rewriteUrlsInDoc);
    base.lawyers     = (base.lawyers || []).map(rewriteUrlsInDoc);
    base.insights    = (base.insights || []).map(rewriteUrlsInDoc);
    base.contact     = base.contact || [];

    store = base;
    return store;
  })();

  return storePromise;
}

function persist() {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(store)); } catch (e) { /* full disk? swallow */ }
  listeners.forEach((fn) => fn());
}

function resetStore() {
  try { localStorage.removeItem(STORAGE_KEY); } catch (e) { /* swallow */ }
  store = null; storePromise = null;
}

// Expose a global helper for the demo so the user can reset if needed.
if (typeof window !== "undefined") {
  window.__cecResetDemoData = resetStore;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const ok = (data) => ({ data, status: 200, statusText: "OK", headers: {}, config: {} });
const fail = (status, msg) => {
  const err = new Error(msg);
  err.response = { status, data: { detail: msg } };
  throw err;
};

function findDept(slug) { return store.departments.find((d) => d.slug === slug); }
function findService(slug) { return store.services.find((s) => s.slug === slug); }
function findLawyer(slug) { return store.lawyers.find((l) => l.slug === slug); }
function findInsight(slug) { return store.insights.find((i) => i.slug === slug); }

function trimDept(d) {
  if (!d) return null;
  return { id: d.id, slug: d.slug, title: d.title };
}
function trimLawyer(l) {
  if (!l) return null;
  return { id: l.id, slug: l.slug, name: l.name, title: l.title, role: l.role, photo_url: l.photo_url, department_id: l.department_id };
}
function trimService(s) {
  if (!s) return null;
  return { id: s.id, slug: s.slug, title: s.title, summary: s.summary, department_id: s.department_id };
}
function trimArticle(a) {
  if (!a) return null;
  return { id: a.id, slug: a.slug, title: a.title, summary: a.summary, image_url: a.image_url, published_at: a.published_at, created_at: a.created_at };
}

function newId() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}
function slugify(s) {
  return String(s || "").toLowerCase().trim()
    .replace(/['"`]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 80);
}
function ensureUniqueSlug(collection, base, ignoreId) {
  let candidate = base || newId();
  let n = 1;
  while (collection.find((x) => x.slug === candidate && x.id !== ignoreId)) {
    n += 1;
    candidate = `${base}-${n}`;
  }
  return candidate;
}

// ---------------------------------------------------------------------------
// Route handler
// ---------------------------------------------------------------------------
async function route(method, urlRaw, payload) {
  await loadStore();

  // strip query
  let url = urlRaw || "";
  let qs = "";
  const qIdx = url.indexOf("?");
  if (qIdx >= 0) { qs = url.slice(qIdx + 1); url = url.slice(0, qIdx); }
  const params = new URLSearchParams(qs);

  // --- Auth ---------------------------------------------------------------
  if (url === "/auth/login" && method === "post") {
    const cfg = await loadAdminCfg();
    const login = cfg && cfg.login;
    // Component sends { email, password } but the field is used as a
    // username in the static demo. Accept either.
    const submitted = (payload?.username || payload?.email || "").trim();
    const password = payload?.password || "";

    if (!login || !login.username || !login.hash) {
      return fail(500, "Admin credentials config missing or invalid.");
    }
    if (submitted.toLowerCase() !== String(login.username).toLowerCase()) {
      return fail(401, "Incorrect username or password.");
    }
    const ok2 = await pbkdf2Verify(password, login);
    if (!ok2) return fail(401, "Incorrect username or password.");
    return ok({
      token: `demo-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`,
      user: { name: "Admin", username: login.username, role: "admin" },
    });
  }
  if (url === "/auth/logout" && method === "post") return ok({ ok: true });
  if (url === "/auth/me" && method === "get") {
    const cfg = await loadAdminCfg();
    const uname = cfg?.login?.username || "admin";
    return ok({ name: "Admin", username: uname, role: "admin" });
  }

  // --- Public: Departments -----------------------------------------------
  if (url === "/departments" && method === "get") {
    return ok(store.departments.map((d) => {
      const services = store.services.filter((s) => s.department_id === d.id);
      const head = d.head_lawyer_id ? store.lawyers.find((l) => l.id === d.head_lawyer_id) : null;
      return { ...d, service_count: services.length, head_lawyer: head ? trimLawyer(head) : null };
    }));
  }
  if (url.startsWith("/departments/") && method === "get") {
    const slug = url.slice("/departments/".length);
    const dept = findDept(slug);
    if (!dept) return fail(404, "Department not found");
    const services = store.services
      .filter((s) => s.department_id === dept.id)
      .sort((a, b) => (a.title || "").localeCompare(b.title || ""));
    const lawyerIds = new Set();
    services.forEach((s) => (s.lawyer_ids || []).forEach((id) => lawyerIds.add(id)));
    if (dept.lawyer_ids) (dept.lawyer_ids || []).forEach((id) => lawyerIds.add(id));
    const lawyers = store.lawyers
      .filter((l) => lawyerIds.has(l.id) || l.department_id === dept.id)
      .map(trimLawyer);
    const head = dept.head_lawyer_id ? trimLawyer(store.lawyers.find((l) => l.id === dept.head_lawyer_id)) : null;
    const articles = store.insights
      .filter((a) => (a.department_ids || []).includes(dept.id))
      .map(trimArticle);
    return ok({
      ...dept,
      services: services.map(trimService),
      lawyers,
      head_lawyer: head,
      articles,
    });
  }

  // --- Public: Services --------------------------------------------------
  if (url === "/services" && method === "get") {
    return ok(store.services.map((s) => ({ ...s, department: trimDept(store.departments.find((d) => d.id === s.department_id)) })));
  }
  if (url.startsWith("/services/") && method === "get") {
    const slug = url.slice("/services/".length);
    const svc = findService(slug);
    if (!svc) return fail(404, "Service not found");
    const dept = svc.department_id ? store.departments.find((d) => d.id === svc.department_id) : null;
    const deptHead = dept && dept.head_lawyer_id ? trimLawyer(store.lawyers.find((l) => l.id === dept.head_lawyer_id)) : null;
    const lawyers = (svc.lawyer_ids || [])
      .map((id) => store.lawyers.find((l) => l.id === id))
      .filter(Boolean)
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
      .map(trimLawyer);
    const articles = store.insights
      .filter((a) => (a.service_ids || []).includes(svc.id) || (svc.article_ids || []).includes(a.id))
      .map(trimArticle);
    const related = dept
      ? store.services.filter((s) => s.department_id === dept.id && s.slug !== slug).slice(0, 12).map(trimService)
      : [];
    return ok({
      ...svc,
      department: trimDept(dept),
      department_head: deptHead,
      lawyers,
      articles,
      related_services: related,
    });
  }

  // --- Public: Lawyers ---------------------------------------------------
  if (url === "/lawyers" && method === "get") {
    const dep = params.get("department");
    const svc = params.get("service");
    let list = [...store.lawyers];
    if (dep) {
      const d = store.departments.find((x) => x.slug === dep);
      if (d) list = list.filter((l) => l.department_id === d.id);
    }
    if (svc) {
      const s = store.services.find((x) => x.slug === svc);
      if (s) list = list.filter((l) => (s.lawyer_ids || []).includes(l.id));
    }
    return ok(list.map((l) => ({ ...l, department: trimDept(store.departments.find((d) => d.id === l.department_id)) })));
  }
  if (url.startsWith("/lawyers/") && method === "get") {
    const slug = url.slice("/lawyers/".length);
    const l = findLawyer(slug);
    if (!l) return fail(404, "Lawyer not found");
    const dep = l.department_id ? store.departments.find((d) => d.id === l.department_id) : null;
    const services = store.services
      .filter((s) => (s.lawyer_ids || []).includes(l.id) || (l.service_ids || []).includes(s.id))
      .map(trimService);
    return ok({ ...l, department: trimDept(dep), services });
  }

  // --- Public: Insights --------------------------------------------------
  if (url === "/insights" && method === "get") {
    const limit = parseInt(params.get("limit") || "0", 10);
    let list = [...store.insights].sort((a, b) => (b.published_at || "").localeCompare(a.published_at || ""));
    if (limit > 0) list = list.slice(0, limit);
    return ok(list);
  }
  if (url.startsWith("/insights/") && method === "get") {
    const slug = url.slice("/insights/".length);
    const a = findInsight(slug);
    if (!a) return fail(404, "Article not found");
    const author = a.author_id ? trimLawyer(store.lawyers.find((l) => l.id === a.author_id)) : null;
    return ok({ ...a, author });
  }

  // --- Public: Contact submit -------------------------------------------
  if (url === "/contact" && method === "post") {
    const id = newId();
    store.contact = store.contact || [];
    store.contact.unshift({ id, ...payload, created_at: new Date().toISOString(), status: "new" });
    persist();
    return ok({ ok: true, id });
  }

  // --- Admin: list views (same as public) -------------------------------
  if (url === "/admin/departments" && method === "get") return ok(store.departments);
  if (url === "/admin/services"    && method === "get") return ok(store.services.map((s) => ({ ...s, department: trimDept(store.departments.find((d) => d.id === s.department_id)) })));
  if (url === "/admin/lawyers"     && method === "get") return ok(store.lawyers.map((l) => ({ ...l, department: trimDept(store.departments.find((d) => d.id === l.department_id)) })));
  if (url === "/admin/insights"    && method === "get") return ok(store.insights);
  if (url === "/admin/contact"     && method === "get") return ok(store.contact || []);

  // --- Admin: mutations --------------------------------------------------
  const adminEntities = {
    "/admin/departments": "departments",
    "/admin/services":    "services",
    "/admin/lawyers":     "lawyers",
    "/admin/insights":    "insights",
  };
  for (const [prefix, key] of Object.entries(adminEntities)) {
    if (url === prefix && method === "post") {
      const titleKey = key === "lawyers" ? "name" : "title";
      const base = slugify(payload?.slug || payload?.[titleKey]) || newId();
      const slug = ensureUniqueSlug(store[key], base);
      const doc = { id: newId(), ...payload, slug, created_at: new Date().toISOString() };
      store[key].unshift(doc);
      persist();
      return ok(doc);
    }
    if (url.startsWith(`${prefix}/`) && method === "put") {
      const id = url.slice(prefix.length + 1);
      const i = store[key].findIndex((x) => x.id === id);
      if (i === -1) return fail(404, "Not found");
      const merged = { ...store[key][i], ...payload, updated_at: new Date().toISOString() };
      if (payload?.slug !== undefined) {
        merged.slug = ensureUniqueSlug(store[key], slugify(payload.slug || ""), id);
      }
      store[key][i] = merged;
      persist();
      return ok(merged);
    }
    if (url.startsWith(`${prefix}/`) && method === "delete") {
      const id = url.slice(prefix.length + 1);
      store[key] = store[key].filter((x) => x.id !== id);
      persist();
      return ok({ ok: true });
    }
  }

  // --- Admin: contact patch ---------------------------------------------
  if (url.startsWith("/admin/contact/") && method === "patch") {
    const id = url.slice("/admin/contact/".length);
    const i = (store.contact || []).findIndex((x) => x.id === id);
    if (i === -1) return fail(404, "Not found");
    store.contact[i] = { ...store.contact[i], ...payload };
    persist();
    return ok(store.contact[i]);
  }
  if (url.startsWith("/admin/contact/") && method === "delete") {
    const id = url.slice("/admin/contact/".length);
    store.contact = (store.contact || []).filter((x) => x.id !== id);
    persist();
    return ok({ ok: true });
  }

  // --- Admin: upload (data-URL for demo) --------------------------------
  if (url === "/admin/upload" && method === "post") {
    // payload is FormData with `file`
    const fd = payload;
    let file = null;
    if (fd && typeof fd.get === "function") file = fd.get("file");
    if (!file) return fail(400, "No file provided");
    const dataUrl = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(file);
    });
    return ok({ url: dataUrl });
  }

  return fail(404, `Static demo: no handler for ${method.toUpperCase()} ${urlRaw}`);
}

// ---------------------------------------------------------------------------
// Axios-shaped client
// ---------------------------------------------------------------------------
function make(method) {
  return (url, a, b) => {
    // axios shapes:
    //   get(url, config?), delete(url, config?)
    //   post(url, data?, config?), put(url, data?, config?), patch(url, data?, config?)
    const payload = (method === "get" || method === "delete") ? undefined : a;
    return route(method, url, payload);
  };
}

export const api = {
  get:    make("get"),
  post:   make("post"),
  put:    make("put"),
  patch:  make("patch"),
  delete: make("delete"),
};

export function authHeader(token) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => (d?.msg || JSON.stringify(d))).join(" ");
  return String(detail);
}

export const API = "(static)";
